import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { cloneDeep, toLower } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { AppState } from 'src/app/app.state';
import { User } from 'src/app/core/user/user.models';
import { selectUIdToken, selectUser } from 'src/app/core/user/user.selectors';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { Dictionary } from 'src/app/shared/types/dictionary';
import { validString } from 'src/app/shared/utilities/types.utils';
import { ApiRequest, ApiResponse } from './api.models';
import { apiConfigurationSelector } from './api.selectors';
import { catchApiError } from './api.utilities';
import { selectOrderMerchantDetails } from '../merchant-details/order-merchant-details/order-merchant-details.selectors';

export const passportClientHeader = 'com-accessopassport-client';

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	/**
	 * The gateway URL
	 */
	private gatewayUrl: string;
	/**
	 * The global gateway URL
	 */
	private globalGatewayUrl: string;
	/**
	 * The configured merchant ID
	 */
	private merchantId: string;
	/**
	 * The current API session id
	 */
	private sessionId: string;
	/**
	 * The name of the current tenant
	 */
	private tenantName: string;
	/**
	 * The configured language
	 */
	private language: string;
	/**
	 * userInformation used for changeCard request
	 */
	private user: User;
	/**
	 * encripted user id token if sent from Passport Support
	 */
	private uidToken: string;
	/**
	 * The current orderMerchantId
	 */
	private orderMerchantId: string;

	/**
	 * Our subject to emit responses on. This is useful in components that need to know about failed responses that are dispatched through effects.
	 */
	private dispatchApiResponse = new Subject<ApiResponse<any>>();
	/**
	 * For unsubscribing when destroying
	 */
	private unsubscribe$ = new Subject<void>();
	/**
	 * An observable that you can subscribe to to receive responses.
	 */
	onResponse$: Observable<ApiResponse<any>> = this.dispatchApiResponse.asObservable();

	/**
	 * @param http Our http client for making requests
	 * @param store The global store
	 */
	constructor(private http: HttpClient, store: Store<AppState>) {
		store
			.pipe(
				takeUntil(this.unsubscribe$),
				select(
					createSelector(
						apiConfigurationSelector,
						selectUser,
						selectUIdToken,
						selectOrderMerchantDetails,
						(apiConfigs, userInfo, uidToken, orderMerchantDetails) => {
							return {
								apiConfigs,
								userInfo,
								uidToken,
								orderMerchantDetails,
							};
						}
					)
				)
			)
			.subscribe(({ apiConfigs, userInfo, uidToken, orderMerchantDetails }) => {
				const { gatewayUrl, tenantName, merchantId: configMerchantId, sessionId, language } = apiConfigs;

				this.gatewayUrl = `${gatewayUrl}${tenantName}`;
				this.globalGatewayUrl = `${gatewayUrl}GLOBAL`;
				this.merchantId = configMerchantId;
				this.sessionId = sessionId;
				this.language = language;
				this.user = userInfo.user;
				this.uidToken = uidToken;
				this.tenantName = tenantName;
				this.orderMerchantId = orderMerchantDetails?.merchantId;
			});
	}

	/**
	 * Does a get request against the gateway
	 * @param params The url params
	 */
	get<T>(params: Dictionary<string>) {
		return this.http.get<T>(this.gatewayUrl, params);
	}

	/**
	 * Does a post request against the gateway
	 * @param requestType The request being made
	 * @param params The post params
	 * @param useGlobal optional, whether to use the global gateway or not
	 */
	post<T>(requestType: string, params: Dictionary<any> = {}, useGlobal = false): Observable<ApiResponse<T>> {
		const { gatewayUrl, globalGatewayUrl } = this;
		const url = useGlobal ? globalGatewayUrl : gatewayUrl;
		const request = this.transform(this.appendDefaultProperties(new ApiRequest(requestType, params)));
		const headers = new HttpHeaders({ [passportClientHeader]: this.tenantName });

		return this.http.post<ApiResponse<T>>(`${url}/${toLower(requestType)}`, request, { headers }).pipe(
			catchApiError<T>(),
			tap((response) => this.dispatchApiResponse.next(response))
		);
	}

	/**
	 * Appends the default properties to an API request (merchant_id, request_type)
	 * @param request The api request to modify
	 */
	appendDefaultProperties(request: ApiRequest): ApiRequest {
		const { sessionId, language } = this;
		request.SERVICE = {
			application_id: 1501,
			machine_id: 1,
			__requester: 'AccountHolderPortal',
			...request.SERVICE,
		};

		if (request.SERVICE.request_type === ApiRequestType.UNIFIED_GUEST_LOGIN) {
			return request;
		}

		if (!request.SERVICE.language) {
			request.SERVICE.language = language;
		}

		if (!request.SERVICE.merchant_id) {
			request.SERVICE.merchant_id = this.merchantId;
		}

		// Booking and Payments need to use the orderMerchantId
		if (
			this.orderMerchantId &&
			this.merchantId !== this.orderMerchantId &&
			(request.SERVICE.request_type === ApiRequestType.ADD_CART_ITEM ||
				request.SERVICE.request_type === ApiRequestType.GET_NEW_CART_ID ||
				request.SERVICE.request_type === ApiRequestType.GET_CART_SUMMARY ||
				request.SERVICE.request_type === ApiRequestType.GET_ENTITLEMENT_KEYWORD_CALENDAR ||
				request.SERVICE.request_type === ApiRequestType.GET_EVENT_DATES_BY_CALENDAR ||
				request.SERVICE.request_type === ApiRequestType.ADD_EDIT_BOOKING ||
				request.SERVICE.request_type === ApiRequestType.GET_DYNAMIC_PRICING_ADJUSTMENT ||
				request.SERVICE.request_type === ApiRequestType.UPDATE_CART_ITEMS)
		) {
			request.SERVICE.merchant_id = this.orderMerchantId;
		}

		// If it's changeCard request, use the merchantId from the order
		if (request.SERVICE.request_type === ApiRequestType.CHANGE_CARD) {
			request.SERVICE.merchant_id = this.user.merchantId;
		}

		if (validString(sessionId)) {
			request.SERVICE.session_id = sessionId;
		}

		if (validString(this.uidToken)) {
			request.SERVICE.uidToken = this.uidToken;
		} else {
			request.SERVICE.user_id = 5;
		}

		return request;
	}

	/**
	 * Appends special properties to specific requests.
	 * @param request The api request to send
	 */
	transform(request: ApiRequest): ApiRequest {
		return cloneDeep(request);
	}
}
